import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { MessageSquare, ChevronDown } from 'lucide-react';
import CommentItem from './CommentItem';
import { Plus } from "lucide-react";

interface Comment {
  id: string;
  user_name: string;
  user_email: string;
  comment_text: string;
  likes: number;
  admin_reply: string | null;
  created_at: string;
  parent_id: string | null;
  replies?: Comment[];
}

interface CommentSectionProps {
  postId: string;
}

const CommentSection = ({ postId }: CommentSectionProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [displayCount, setDisplayCount] = useState(2);
  const { toast } = useToast();

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Build nested comment structure
      const commentMap: { [key: string]: Comment } = {};
      const rootComments: Comment[] = [];

      // First pass: create comment objects
      (data || []).forEach(comment => {
        commentMap[comment.id] = { ...comment, replies: [] };
      });

      // Second pass: build hierarchy
      (data || []).forEach(comment => {
        if (comment.parent_id && commentMap[comment.parent_id]) {
          commentMap[comment.parent_id].replies!.push(commentMap[comment.id]);
        } else if (!comment.parent_id) {
          rootComments.push(commentMap[comment.id]);
        }
      });

      setComments(rootComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !commentText.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_name: name.trim(),
          user_email: email.trim(),
          comment_text: commentText.trim(),
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Your comment has been posted!',
      });

      setName('');
      setEmail('');
      setCommentText('');
      fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to post comment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const visibleComments = comments.slice(0, displayCount);
  const hasMore = comments.length > displayCount;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-bold gradient-text">
          Comments ({comments.length})
        </h2>
        
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2 group"
        >
          {showForm ? 'Hide' : 'Add'}
          <div className="relative w-4 h-4 overflow-hidden">
            <Plus 
              className={`w-4 h-4 absolute transition-all duration-300 ${
                showForm 
                  ? 'rotate-45 opacity-100' 
                  : 'rotate-0 opacity-100'
              }`}
            />
          </div>
        </button>
      </div>

      {/* Comment Form - Collapsible */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showForm 
            ? 'max-h-[800px] opacity-100 mt-4' 
            : 'max-h-0 opacity-0'
        }`}
      >
        <div className="glass rounded-xl p-4 sm:p-5">
          <h3 className="text-base sm:text-lg font-semibold mb-3">Leave a Comment</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Your Name *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
                className="bg-background/50 text-sm"
              />
              <Input
                type="email"
                placeholder="Your Email *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                required
                className="bg-background/50 text-sm"
              />
            </div>
            <Textarea
              placeholder="Your Comment *"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={1000}
              required
              className="min-h-[80px] sm:min-h-[100px] bg-background/50 text-sm"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 text-sm"
                size="sm"
              >
                {loading ? 'Posting...' : 'Post'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 glass rounded-xl">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground text-sm">No comments yet. Be the first!</p>
          </div>
        ) : (
          <>
            {visibleComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                postId={postId}
                onCommentAdded={fetchComments}
                level={0}
              />
            ))}
            
            {hasMore && (
              <div className="flex justify-center pt-3">
                <button
                  onClick={() => setDisplayCount(prev => prev + 2)}
                  className="glass-morphism px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-primary transition-all flex items-center gap-2"
                >
                  
                  Load {Math.min(5, comments.length - displayCount)} More Comment
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
